import { useState, useMemo, useCallback, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Radio,
    Button,
    Stack,
    IconButton,
    alpha,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
    useGetCountryListQuery,
    useGetForeignStates2Query,
    useGetCityListByProvinceOrStates2Query,
    useGetCountiesByStateMutation,
} from "Services/CountryService";
import useIsUsAccount from "hooks/useIsUsAccount";
import { extractStateOrProvinceFromAbbr } from "views/document-generator-v2/utils";
import MultiSelectDropdown from "components/MultiSelectDropdown";
import { useTranslation } from "react-i18next";

const TypeOptionCard = ({ title, subtitle, selected, onClick }) => (
    <Box
        onClick={onClick}
        sx={{
            border: "1.5px solid",
            borderColor: selected ? "secondary.main" : "#E2E8F0",
            backgroundColor: selected ? "secondary.light" : "#FFFFFF",
            borderRadius: 2.5,
            p: 2,
            cursor: "pointer",
            display: "flex",
            gap: 1.5,
        }}
    >
        <Radio checked={selected} color="secondary" sx={{ p: 0 }} />
        <Box>
            <Typography
                variant="subtitle1"
                color="text.mediumDark"
                sx={{ fontWeight: 600, fontSize: "0.95rem" }}
            >
                {title}
            </Typography>
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.825rem" }}
            >
                {subtitle}
            </Typography>
        </Box>
    </Box>
);

export const AreaTag = ({ area, onRemove }) => (
    <Box
        sx={{
            display: "inline-flex",
            alignItems: "center",
            backgroundColor: "secondary.light",
            borderRadius: 5,
            p: 1.2,
            gap: 1.2,
            border: (theme) =>
                `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
        }}
    >
        <Box
            sx={{
                backgroundColor: "secondary.main",
                color: "#FFFFFF",
                fontSize: "0.68rem",
                fontWeight: 600,
                px: 1.2,
                py: 0.4,
                borderRadius: 1,
                lineHeight: 1.2,
            }}
        >
            {area.scope === "country" && "COUNTRY"}
            {area.scope === "region" && "REGION"}
            {area.scope === "city" && "CITY"}
            {area.scope === "county" && "COUNTY"}
        </Box>

        <Typography variant="body2" sx={{ color: "rgb(0 83 75)" }}>
            {area.scope === "country" && area.country}
            {area.scope === "region" && area.region}
            {area.scope === "city" && `${area.city}, ${area.region}`}
            {area.scope === "county" && `${area.county}, ${area.region}`}
        </Typography>
        {onRemove && (
            <IconButton
                size="small"
                onClick={() => onRemove(area.id)}
                sx={{ color: "rgb(0 83 75)", p: 0.2 }}
            >
                <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
        )}
    </Box>
);

// Country/state/city area picker (entire country vs. specific region/city), shared between the
// Organization Profile stepper and the User Project "Geographic Areas Served" field. Presentational:
// takes the current areas and reports the full next array via onChange, same convention as
// AutocompleteWithTags, instead of owning any Redux state itself.
const GeographicAreaPicker = ({ addedAreas = [], onChange, showAddedAreas = true }) => {
    const { t } = useTranslation();
    const isUs = useIsUsAccount();

    const [selectedType, setSelectedType] = useState("specific");
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [selectedRegions, setSelectedRegions] = useState([]);
    const [selectedCities, setSelectedCities] = useState([]);
    const [selectedCounties, setSelectedCounties] = useState([]);
    const [countyOptions, setCountyOptions] = useState([]);
    const [isFetchingCounties, setIsFetchingCounties] = useState(false);

    const { isLoading: isCountryLoading, data: countries = [] } =
        useGetCountryListQuery();
    const { data: regions = [], isLoading: isRegionsLoading } =
        useGetForeignStates2Query(isUs ? "US" : "CA");
    const { data: cities = [], isFetching } =
        useGetCityListByProvinceOrStates2Query(selectedRegions);
    const [getCountiesByState] = useGetCountiesByStateMutation();

    // Counties (backed by the seeded UsCity table) only exist for US states, mirroring the same
    // isUs gate SimilarCharitiesTagInput uses for its county filter. Fetch the distinct county
    // list per selected state and flatten into options that keep the owning state attached, since
    // the same county name can recur across states.
    useEffect(() => {
        if (!isUs || selectedRegions.length === 0) {
            setCountyOptions([]);
            setSelectedCounties([]);
            return;
        }

        let cancelled = false;
        setIsFetchingCounties(true);

        Promise.all(
            selectedRegions.map((stateCode) =>
                getCountiesByState(stateCode)
                    .unwrap()
                    .then((counties) =>
                        counties.map((countyName) => ({
                            key: stateCode + "|" + countyName,
                            value: countyName,
                            stateCode,
                            region:
                                regions.find((s) => s.code === stateCode)
                                    ?.description ?? stateCode,
                        }))
                    )
                    .catch(() => [])
            )
        ).then((results) => {
            if (cancelled) return;
            setCountyOptions(results.flat());
            setIsFetchingCounties(false);
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUs, selectedRegions]);

    const filterAvailable = useCallback(
        (items, keySelector = (item) => item) => {
            const existingKeys = new Set(
                addedAreas.map((a) => {
                    if (a.scope === "country") return a.country;
                    if (a.scope === "region") return a.region;
                    if (a.scope === "city") return a.region + "|" + a.city;
                    if (a.scope === "county")
                        return a.region + "|county|" + a.county;
                    return null;
                })
            );
            return items.filter((item) => !existingKeys.has(keySelector(item)));
        },
        [addedAreas]
    );

    const availableCountries = useMemo(
        () => filterAvailable(countries, (item) => item.description),
        [countries, filterAvailable]
    );

    const availableCities = useMemo(
        () =>
            filterAvailable(
                cities,
                (item) =>
                    extractStateOrProvinceFromAbbr(item.secondaryAction) +
                    "|" +
                    item.value
            ),
        [cities, filterAvailable]
    );

    const availableCounties = useMemo(
        () =>
            filterAvailable(
                countyOptions,
                (item) => item.region + "|county|" + item.value
            ),
        [countyOptions, filterAvailable]
    );

    const handleAddArea = () => {
        const newItems = [];
        if (selectedType === "entire") {
            selectedCountries.forEach((country) => {
                const countryObj = countries.find(
                    (s) => s.description === country
                );

                const id = "country-" + countryObj.code;
                if (addedAreas.findIndex((a) => a.id === id) === -1) {
                    newItems.push({
                        id,
                        scope: "country",
                        country: countryObj.description,
                    });
                }
            });
            setSelectedCountries([]);
        } else {
            selectedRegions.forEach((provinceCode) => {
                const provinceObj = regions.find(
                    (s) => s.code === provinceCode
                );
                const id = "region-" + provinceCode;
                if (addedAreas.findIndex((a) => a.id === id) === -1) {
                    newItems.push({
                        id,
                        scope: "region",
                        country: isUs ? "United States" : "Canada",
                        region: provinceObj.description,
                    });
                }
            });

            selectedCities.forEach((city) => {
                const cityObj = cities.find((c) => c.value === city);

                const id =
                    "city-" + cityObj.value + "-" + cityObj.secondaryAction;
                if (addedAreas.findIndex((a) => a.id === id) === -1) {
                    newItems.push({
                        id:
                            "city-" +
                            cityObj.value +
                            "-" +
                            cityObj.secondaryAction,
                        scope: "city",
                        country: isUs ? "United States" : "Canada",
                        region: extractStateOrProvinceFromAbbr(
                            cityObj.secondaryAction
                        ),
                        city: cityObj.value,
                    });
                }
            });

            selectedCounties.forEach((key) => {
                const countyObj = countyOptions.find((c) => c.key === key);
                if (!countyObj) return;

                const id = "county-" + countyObj.stateCode + "-" + countyObj.value;
                if (addedAreas.findIndex((a) => a.id === id) === -1) {
                    newItems.push({
                        id,
                        scope: "county",
                        country: isUs ? "United States" : "Canada",
                        region: countyObj.region,
                        county: countyObj.value,
                    });
                }
            });

            setSelectedRegions([]);
            setSelectedCities([]);
            setSelectedCounties([]);
        }

        if (newItems.length > 0) {
            onChange?.([...addedAreas, ...newItems]);
        }
    };

    const handleRemoveArea = (idToRemove) => {
        onChange?.(addedAreas.filter((item) => item.id !== idToRemove));
    };

    const isAddDisabled =
        selectedType === "entire"
            ? selectedCountries.length === 0
            : selectedRegions.length === 0 &&
              selectedCities.length === 0 &&
              selectedCounties.length === 0;

    return (
        <Box>
            <Card
                variant="outlined"
                sx={{ borderRadius: 3, borderColor: "#E2E8F0", mb: showAddedAreas ? 4 : 0 }}
            >
                <CardContent sx={{ p: 3.5 }}>
                    {/* Type Switcher */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <TypeOptionCard
                                title={t("labels.specific_area")}
                                subtitle={t("labels.a_region_a_city_or_both", {
                                    orgRegion: isUs ? "state" : "province",
                                })}
                                selected={selectedType === "specific"}
                                onClick={() => setSelectedType("specific")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TypeOptionCard
                                title={t("labels.entire_country")}
                                subtitle={t(
                                    "labels.everywhere_within_one_country"
                                )}
                                selected={selectedType === "entire"}
                                onClick={() => setSelectedType("entire")}
                            />
                        </Grid>
                    </Grid>

                    {/* Conditional Inputs */}
                    {selectedType === "entire" ? (
                        <Box sx={{ mb: 3 }}>
                            <MultiSelectDropdown
                                label={t("labels.country")}
                                placeholder={t("labels.select_a_country")}
                                value={selectedCountries}
                                options={availableCountries}
                                getOptionLabel={(c) => c.description}
                                getOptionValue={(c) => c.description}
                                onChange={(e) =>
                                    setSelectedCountries(e.target.value)
                                }
                                loading={isCountryLoading}
                                required
                            />
                        </Box>
                    ) : (
                        <Box sx={{ mb: 2 }}>
                            <Grid container spacing={2} sx={{ mb: 1.5 }}>
                                <Grid item xs={12} sm={isUs ? 4 : 6}>
                                    <MultiSelectDropdown
                                        label={
                                            isUs
                                                ? t("labels.state")
                                                : t("labels.province")
                                        }
                                        placeholder={
                                            isUs
                                                ? t("labels.all_states")
                                                : t("labels.all_provinces")
                                        }
                                        value={selectedRegions}
                                        options={regions}
                                        getOptionLabel={(s) => s.description}
                                        getOptionValue={(s) => s.code}
                                        onChange={(e) =>
                                            setSelectedRegions(e.target.value)
                                        }
                                        loading={isRegionsLoading}
                                    />
                                </Grid>
                                {isUs && (
                                    <Grid item xs={12} sm={4}>
                                        <MultiSelectDropdown
                                            label={t("labels.county")}
                                            placeholder={t(
                                                "labels.all_counties"
                                            )}
                                            value={selectedCounties}
                                            options={availableCounties}
                                            getOptionLabel={(c) => c.value}
                                            getOptionValue={(c) => c.key}
                                            onChange={(e) =>
                                                setSelectedCounties(
                                                    e.target.value
                                                )
                                            }
                                            disabled={
                                                selectedRegions.length === 0
                                            }
                                            loading={isFetchingCounties}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={isUs ? 4 : 6}>
                                    <MultiSelectDropdown
                                        label={t("labels.city")}
                                        placeholder={t("labels.all_cities")}
                                        value={selectedCities}
                                        options={availableCities}
                                        getOptionLabel={(s) => s.value}
                                        getOptionValue={(s) => s.value}
                                        onChange={(e) =>
                                            setSelectedCities(e.target.value)
                                        }
                                        loading={isFetching}
                                    />
                                </Grid>
                            </Grid>
                            <Typography variant="caption" fontSize="0.75rem">
                                {t("messages.specific_area_description", {
                                    orgCountry: isUs
                                        ? "United States"
                                        : "Canada",
                                })}
                            </Typography>
                        </Box>
                    )}

                    {/* Add Area Action */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            mt: 2,
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<AddIcon />}
                            onClick={handleAddArea}
                            disabled={isAddDisabled}
                            sx={{
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                            }}
                        >
                            {t("labels.add_area")}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {showAddedAreas && (
                <Box sx={{ mb: 6 }}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 2 }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 600,
                                color: "#334155",
                                textTransform: "uppercase",
                            }}
                        >
                            {t("labels.areas_added")}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 400 }}
                        >
                            {addedAreas.length} added
                        </Typography>
                    </Stack>

                    <Stack direction="row" flexWrap="wrap" gap={1.5}>
                        {addedAreas.map((area) => (
                            <AreaTag
                                key={area.id}
                                area={area}
                                onRemove={handleRemoveArea}
                            />
                        ))}
                    </Stack>
                </Box>
            )}
        </Box>
    );
};

export default GeographicAreaPicker;
